<template>
  <div class="orders-table">
    <div class="orders-table__dropdown">
      <span>
        {{ $t("services.orders.orderStatus") }}
        <span style="color: var(--orange)">*</span>
      </span>
      <Dropdown
        v-model="statuses"
        :title="$t('services.orders.statusSelectTitle')"
        :label-name="statusesLabelName"
      />
    </div>

    <Popup
      v-if="orderToBeDeleted"
      remove
      @confirm="cancelOrder(rowForDel)"
      @close="canselDeletingOrder(rowForDel)"
    >
      {{ $t("events.popupOrderQuestion") }}
      <template v-slot:delete> {{ $t("events.popupOrderDelete") }} </template>
      <template v-slot:cancel> {{ $t("events.popupOrderCancel") }} </template>
    </Popup>

    <Table
      :columns="columns"
      :rows="rows"
      :showServiceDescr="true"
      class="orders-page-table"
    >
      <template #td="{ row, col, i, clickHandler }">
        <td v-if="col.label === 'drop-action'" style="width: 60px">
          <button
            type="button"
            class="table__open"
            :style="{
              display: 'inline-flex',
              transform: visible === i ? 'rotate(180deg)' : 'none'
            }"
            @click="showDrop(i, clickHandler)"
          >
            <icon name="arrow-down" />
          </button>
        </td>
        <td v-else-if="col.label === 'status'">
          <div class="orders-page-table__status">
            <span :class="getStatusColor(row.status)">
              {{ getStatus(row.status) | localeField("title") }}
            </span>
          </div>
        </td>
        <td v-else-if="col.label === 'stand'">
          {{ row.stand ? row.stand : "-" }}
        </td>
        <td v-else-if="col.label === 'summ'">
          {{ getSumm(row) | formatPrice({ currency: row.currency }) }}
        </td>
        <td v-else-if="col.label === 'chat-action'" style="width: 60px">
          <button
            type="button"
            class="orders-page-table__chat"
            style="display: inline-flex"
            @click.prevent="showChat(row)"
          >
            <span
              class="orders-page-count__value"
              v-if="amountUnreadMessages(row) > 0"
            >
              {{ amountUnreadMessages(row) }}
            </span>
            <icon name="chat" />
          </button>
        </td>
        <td
          v-else-if="col.label === 'action'"
          style="width: 80px; text-align: right"
        >
          <DropMenuSelect class="drop-select_orders">
            <a
              href="#"
              v-if="row.status === 'waiting_confirmation' && isExhibitionActive"
              @click.prevent="confirmOrder(row)"
            >
              {{ $t("services.orders.confirmOrder") }}
            </a>
            <a
              href="#"
              v-if="
                !['executed', 'canceled'].includes(row.status) &&
                  isExhibitionActive
              "
              @click.prevent="showWarnPopup(row)"
            >
              {{ $t("services.orders.cancelOrder") }}
            </a>
            <a href="#" @click.prevent="downloadOrder(row)">
              {{ $t("services.orders.downloadOrder") }}
            </a>
            <a
              v-if="row.is_have_documents"
              href="#"
              @click.prevent="downloadInvioce(row)"
            >
              {{ $t("services.orders.downloadInvoice") }}
            </a>
          </DropMenuSelect>
        </td>
        <td v-else>
          <span>
            {{ row[col.label] ? row[col.label] : "-" }}
          </span>
        </td>
      </template>
      <template #m-row="{ row, columns, i}">
        <td :colspan="columns.length">
          <div class="table__form">
            <div
              class="table__form-row"
              v-for="(col, i) in mobileCols"
              :key="i"
            >
              <template v-if="col.label === 'status'">
                <span>{{ col | localeField("title") }}</span>
                <div class="orders-page-table__status">
                  <span :class="getStatusColor(row.status)">
                    {{ getStatus(row.status) | localeField("title") }}
                  </span>
                </div>
              </template>
              <template v-else-if="col.label === 'stand'">
                <span>{{ col | localeField("title") }}</span>
                <div>{{ row.stand ? row.stand : "-" }}</div>
              </template>
              <template v-else>
                <span>{{ col | localeField("title") }}</span>
                <div>{{ row[col.label] ? row[col.label] : "-" }}</div>
              </template>
            </div>
          </div>
        </td>
      </template>
      <template #row-extension="{ row, columns, i }">
        <tr v-if="i === visible">
          <td
            :colspan="columns.length"
            class="orders-page-table__expand table__expand"
          >
            <Table :rows="row.services" :columns="columnsChild">
              <template #td="{ row, col, i, clickHandler }">
                <td v-if="col.label === 'title'">
                  <div class="title-field">
                    <button
                      type="button"
                      class="table__open"
                      @click="clickHandler(i)"
                    >
                      <icon name="arrow-down" />
                    </button>
                    <div>
                      <span>
                        {{ row.service | localeField("name") }}
                      </span>
                      <span>
                        {{ row.comment }}
                      </span>
                    </div>
                  </div>
                </td>
                <td v-else-if="col.label === 'unit'">
                  <span>
                    {{ row.unit | localeField("name", "-") }}
                  </span>
                </td>
                <td v-else-if="col.label === 'price'" style="width: 85px">
                  <span class="table-child-price__no-white-space">{{
                    row.price_with_vat | formatPrice({ currency: row.currency })
                  }}</span>
                </td>
                <td v-else-if="col.label === 'summ'" style="width: 85px">
                  <span>{{
                    row.amount_with_vat
                      | formatPrice({ currency: row.currency })
                  }}</span>
                </td>
                <td v-else-if="col.label === 'date'">
                  <span>
                    <DatePicker
                      :value="getServiceDate(row)"
                      :available-periods="getServiceAvailablePeriods(row)"
                      is-range
                      :key="`datepicker${row.id}`"
                      align-right
                      disabled
                    >
                      <template #input="{ visibilityToggler }">
                        <button
                          type="button"
                          class="primary-button date-button"
                          @click="visibilityToggler()"
                        >
                          <icon name="calendar" />
                          <span class="date-button__counter">
                            {{ selectedDatesCount(row) }}
                          </span>
                        </button>
                      </template>
                    </DatePicker>
                  </span>
                </td>
                <td v-else>
                  <span>
                    {{ row[col.label] ? row[col.label] : "-" }}
                  </span>
                </td>
              </template>

              <template #m-row="{ row, columns, i}">
                <td :colspan="columns.length">
                  <div class="table__form">
                    <div
                      class="table__form-row"
                      v-for="(col, i) in columns.filter(c => !c.showMobile)"
                      :key="i"
                    >
                      <template v-if="col.label === 'date'">
                        <span>{{ col | localeField("title") }}</span>
                        <DatePicker
                          :value="getServiceDate(row)"
                          :available-periods="getServiceAvailablePeriods(row)"
                          is-range
                          align-right
                          disabled
                        >
                          <template #input="{ visibilityToggler }">
                            <button
                              type="button"
                              class="primary-button date-button"
                              @click="visibilityToggler()"
                            >
                              <icon name="calendar" />
                              <span class="date-button__counter">
                                {{ selectedDatesCount(row) }}
                              </span>
                            </button>
                          </template>
                        </DatePicker>
                      </template>
                      <template v-else-if="col.label === 'unit'">
                        <span>{{ col | localeField("title") }}</span>
                        <div>
                          {{ row.unit | localeField("name", "-") }}
                        </div>
                      </template>
                      <template v-else-if="col.label === 'price'">
                        <span>{{ col | localeField("title") }}</span>
                        <div>
                          {{
                            row.price_with_vat
                              | formatPrice({ currency: row.currency })
                          }}
                        </div>
                      </template>
                      <template v-else-if="col.label === 'summ'">
                        <span>{{ col | localeField("title") }}</span>
                        <div>
                          {{
                            row.amount_with_vat
                              | formatPrice({ currency: row.currency })
                          }}
                        </div>
                      </template>
                      <template v-else>
                        <span>{{ col | localeField("title") }}</span>
                        <div>{{ row[col.label] ? row[col.label] : "-" }}</div>
                      </template>
                    </div>
                  </div>
                </td>
              </template>
            </Table>
            <div v-if="row.is_edo">
              <checkbox v-model="row.is_edo" disabled>
                {{ $t("services.ordering.is_edo") }}
              </checkbox>
            </div>

            <div class="responsible" v-if="row.manager && row.status !== 'new'">
              <span class="responsible__title">
                {{ $t("services.orders.managerTitle") }}:
              </span>
              <div class="responsible__person">
                <img
                  v-if="row.manager.image"
                  :src="row.manager.image.thumbnail_path | image"
                  :alt="getPersonName(row.manager)"
                />
                <div class="responsible__person-data">
                  <span class="responsible__person-name">
                    {{ getPersonName(row.manager) }}
                  </span>
                  <div
                    class="responsible__person-contacts"
                    v-if="row.manager.email"
                  >
                    <span>E-mail:</span>
                    <span>{{ row.manager.email }}</span>
                  </div>
                  <div
                    class="responsible__person-contacts"
                    v-if="row.manager.phone"
                  >
                    <span>{{ $t("services.orders.managerPhone") }}:</span>
                    <span>{{ row.manager.phone }}</span>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </Table>
  </div>
</template>

<script>
import moment from "moment";
import apiService from "@/api/services";
import DropMenuSelect from "@/components/Custom/DropSelectMenu.vue";
import {
  Dropdown,
  Checkbox,
  DatePicker,
  Popup,
  Table
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";

export default {
  name: "OrdersTable",
  components: {
    Checkbox,
    DatePicker,
    Popup,
    DropMenuSelect,
    Table,
    Dropdown
  },
  props: {
    items: Array,
    executors: Array,
    isExhibitionActive: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    orderToBeDeleted: false,
    rowForDel: null,
    statuses: [
      { id: "new", title_rus: "Оформлен", title_eng: "New" },
      {
        id: "processing",
        title_rus: "Обрабатывается",
        title_eng: "Processing"
      },
      {
        id: "waiting_confirmation",
        title_rus: "Требуется подтверждение",
        title_eng: "Waiting confirmation"
      },
      { id: "confirmed", title_rus: "Подтвержден", title_eng: "Confirmed" },
      { id: "executed", title_rus: "Исполнен", title_eng: "Executed" },
      { id: "canceled", title_rus: "Отменен", title_eng: "Canceled" }
    ],
    executor: null,
    loading: true,
    status: null,
    visible: null,
    columns: [
      {
        label: "drop-action",
        showMobile: true
      },
      {
        label: "id",
        title_rus: "№ заказа",
        title_eng: "Order №",
        showMobile: true
      },
      {
        label: "stand",
        title_rus: "Стенд",
        title_eng: "Stand"
      },
      {
        label: "created_at",
        title_rus: "Дата",
        title_eng: "Date"
      },
      {
        label: "status",
        title_rus: "Статус",
        title_eng: "Status"
      },
      {
        label: "summ",
        title_rus: "Сумма (с НДС)",
        title_eng: "Cost (with VAT)",
        showMobile: true
      },
      {
        label: "chat-action",
        title_rus: "Чат",
        title_eng: "Chat",
        showMobile: true
      },
      {
        label: "action",
        title_rus: "Действия",
        title_eng: "Actions",
        showMobile: true
      }
    ],
    columnsChild: [
      {
        label: "title",
        title_rus: "Наименование",
        title_eng: "Name",
        showMobile: true
      },
      {
        label: "unit",
        title_rus: "Ед. изм.",
        title_eng: "Unit"
      },
      {
        label: "price",
        title_rus: "Цена",
        title_eng: "Price"
      },
      {
        label: "quantity",
        title_rus: "Количество",
        title_eng: "Number"
      },
      {
        label: "date",
        title_rus: "Дата",
        title_eng: "Date"
      },
      {
        label: "summ",
        title_rus: "Стоимость (с НДС)",
        title_eng: "Cost (incl. VAT)",
        showMobile: true
      }
    ]
  }),
  computed: {
    rows() {
      let items = this.items;
      let statuses = this.statuses.filter(s => s.selected);
      if (statuses.length) {
        items = items.filter(i => statuses.map(s => s.id).includes(i.status));
      }

      if (this.executor) {
        items = items.filter(i => i.executor.id === this.executor);
      }
      return items;
    },
    mobileCols() {
      return this.columns.filter(
        c => !c.showMobile && this.$options.filters.localeField(c, "title")
      );
    },
    statusesLabelName() {
      return i18n.locale === "ru" ? "title_rus" : "title_eng";
    }
  },
  methods: {
    showWarnPopup(order) {
      this.orderToBeDeleted = true;
      this.rowForDel = order;
    },
    amountUnreadMessages(row) {
      return row.unread_messages_count || 0;
    },
    confirmOrder(order) {
      if (order) {
        apiService
          .confirmAdditionalOrder(this.$route.params.id, order.id)
          .then(() => {
            this.$emit("reload");
            this.$modal.show("alert-modal", {
              data: {
                title_rus: "Подтверждение заказа!",
                title_eng: "Order confirmation!",
                text_rus: `Вы подтвердили изменения в заказе № ${order.id}`,
                text_eng: `You confirmed order changes № ${order.id}`,
                warning_rus:
                  "Уведомление о выставленном счете на оплату услуг придет на E-mail.",
                warning_eng:
                  "Notification of the invoice for payment for services will be sent to your e-mail."
              }
            });
          });
      }
    },
    cancelOrder(order) {
      if (order) {
        apiService
          .cancelAdditionalOrder(this.$route.params.id, order.id)
          .then(() => {
            this.$emit("reload");
          });
      }
    },
    canselDeletingOrder() {
      this.orderToBeDeleted = false;
    },
    downloadInvioce(order) {
      if (order) {
        apiService
          .downloadAdditionalOrderInvoice(this.$route.params.id, order.id)
          .then(response => {
            let blob = new Blob([response], {
              type: "application/zip"
            });
            const url = window.URL.createObjectURL(blob);
            let link = document.createElement("a");
            link.setAttribute("target", "_blank");
            link.href = url;
            i18n.locale === "en"
              ? (link.download = `Order No ${order.id} as of ${order.created_at}.zip`)
              : (link.download = `Заказ № ${order.id} от ${order.created_at}.zip`);
            link.click();
          });
      }
    },
    downloadOrder(order) {
      if (order) {
        apiService
          .downloadAdditionalOrder(this.$route.params.id, order.id)
          .then(response => {
            let blob = new Blob([response], {
              type: "application/pdf"
            });
            const url = window.URL.createObjectURL(blob);
            let link = document.createElement("a");
            link.setAttribute("target", "_blank");
            link.href = url;
            i18n.locale === "en"
              ? (link.download = `Order No ${order.id} as of ${order.created_at}.pdf`)
              : (link.download = `Заказ № ${order.id} от ${order.created_at}.pdf`);
            link.click();
          });
      }
    },
    getPersonName(person) {
      if (i18n.locale === "en") {
        return `${person.lastname_eng || ""} ${person.name_eng ||
          ""} ${person.middlename_eng || ""}`;
      } else {
        return `${person.lastname_rus} ${person.name_rus ||
          ""} ${person.middlename_rus || ""}`;
      }
    },
    getSumm(order) {
      return order.services
        .map(s => s.amount_with_vat)
        .reduce((a, b) => a + b, 0);
    },
    getStatus(id) {
      return this.statuses.find(i => i.id === id);
    },
    getStatusColor(id) {
      let cls = "status ";
      switch (id) {
        case "waiting_confirmation":
          cls += "status--orange";
          break;
        case "new":
        case "processing":
        case "confirmed":
        case "executed":
          cls += "status--blue";
          break;
        case "canceled":
        default:
          break;
      }
      return cls;
    },
    showChat(order) {
      this.$modal.show("chat-modal", { order: order, statuses: this.statuses });
    },
    showDrop(i, handler) {
      handler(i);
      if (this.visible === i) {
        this.visible = null;
        return null;
      }
      this.visible = i;
    },
    selectedDatesCount(service) {
      return service.dates ? service.dates.length : 0;
    },

    getServiceDate(service) {
      return (
        service.dates?.map(date => moment(date).format("YYYY-MM-DD")) || []
      );
    },
    getServiceAvailablePeriods(service) {
      return [
        {
          startDate: moment.min(service.dates?.map(item => moment(item))),
          endDate: moment.max(service.dates?.map(item => moment(item))),
          type: "ordered"
        }
      ];
    }
  }
};
</script>

<style lang="scss">
.order-delete-popup .popup-delete__inner {
  margin-left: 0;
  width: 100%;
}
.orders-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  & thead {
    position: sticky;
    top: 56px;
    background: #fff;
    z-index: 10;
  }
  &__select {
    height: 22px;
    background: none;
    border: none;
    color: var(--dark);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    cursor: pointer;
  }

  &__dropdown {
    max-width: 260px;
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;
    @media (max-width: 740px) {
      margin-bottom: 15px;
    }

    & > span {
      margin-bottom: 5px;
      color: var(--dark-light);
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
    }

    & > * {
      width: 100%;
    }
  }
  .drop-select_orders .drop-select {
    min-width: 138px;
  }
  & .orders-page-count__value {
    position: absolute;
    right: 10px;
    top: 6px;
    transform: translate(50%, -35%);
    background-color: #005197;
    color: var(--white, #fff);
    border-radius: 50%;
    min-width: 15px;
    height: 15px;
    padding: 1px;
    font-size: 7px;
    font-weight: 500;
    line-height: normal;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    box-sizing: content-box;
  }
}
.title-field {
  display: flex;
  align-items: center;

  > button {
    margin-right: 10px;
  }

  > div {
    display: flex;
    flex-direction: column;
  }

  span:first-child {
    margin-bottom: 5px;
  }

  span:last-child {
    color: var(--dark-light);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }
}
.table-child-price__no-white-space {
  white-space: nowrap;
}
.date-button {
  background: var(--gray-30);
  border: 1px solid var(--gray-darker);
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 8px;

  &:focus,
  &:hover {
    background: var(--gray-30);
  }

  .icon {
    display: inline-flex;
    position: relative;
  }

  &--wide {
    width: 100%;
  }

  &--wide &-counter {
    right: -4px;
    top: -4px;
  }

  .svg-icon {
    transition: 0.3s;
    color: var(--gray-dark);
    width: 20px;
    height: 20px;
    margin: 0 !important;
  }

  &__counter {
    position: absolute;
    right: 0;
    top: 0;
    transform: translate(50%, -50%);
    color: var(--white);
    background: var(--gray-darker);
    border-radius: 100%;
    width: 15px;
    height: 15px;
    border: 1px solid var(--white);
    font-size: 9px;
    font-style: normal;
    font-weight: 500;
    line-height: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
